import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { api } from "@/lib/api";
import OnePagerDokument, { MODES } from "./OnePagerDokument";
import "./onepager.css";

/* Offentlig, passordbeskyttet delt visning av Executive One-Pager.
   Modusen er låst til lenken — mottakeren ser kun sin brief. */
export default function OnePagerShare() {
  const { token } = useParams();
  const [status, setStatus] = useState("laster"); // laster | laast | aapen | borte
  const [eventName, setEventName] = useState("Norwegian AI Summit 2027");
  const [mode, setMode] = useState("general");
  const [ev, setEv] = useState(null);
  const [kode, setKode] = useState("");
  const [kodeFeil, setKodeFeil] = useState("");
  const [sjekker, setSjekker] = useState(false);

  useEffect(() => {
    document.title = "Norwegian AI Summit 2027 — The Next Shift";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    let aktiv = true;
    api.get(`/one-pager/s/${token}`)
      .then((r) => {
        if (!aktiv) return;
        setEventName(r.data.eventName || "Norwegian AI Summit 2027");
        setMode(r.data.mode || "general");
        setStatus("laast");
      })
      .catch(() => { if (aktiv) setStatus("borte"); });
    return () => { aktiv = false; };
  }, [token]);

  const laasOpp = async (e) => {
    e?.preventDefault();
    if (!kode.trim() || sjekker) return;
    setSjekker(true);
    setKodeFeil("");
    try {
      const r = await api.post(`/one-pager/s/${token}/unlock`, { code: kode.trim().toUpperCase() });
      setMode(r.data.mode || "general");
      setEv(r.data.data?.event || null);
      setStatus("aapen");
    } catch (err) {
      setKodeFeil(err?.response?.status === 403 ? "Wrong access code — try again." : "Something went wrong. Please try again.");
    } finally {
      setSjekker(false);
    }
  };

  if (status === "aapen") {
    return (
      <OnePagerDokument
        mode={mode}
        ev={ev}
        chrome={(
          <button type="button" className="op-pdf op-chrome" onClick={() => window.print()} data-testid="op-share-print">
            <Printer size={11} /> Print / PDF
          </button>
        )}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#070a0e] flex items-center justify-center px-5 text-center">
      {status === "laster" && (
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#565c66]">Loading …</p>
      )}
      {status === "borte" && (
        <div data-testid="op-share-borte">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#565c66]">Norwegian AI Summit 2027</p>
          <p className="text-[18px] font-semibold text-[#f5f1e8] mt-3">This link is no longer active.</p>
        </div>
      )}
      {status === "laast" && (
        <div className="w-full max-w-[380px]" data-testid="op-share-gate">
          <p className="font-mono text-[9px] uppercase tracking-[0.34em] text-[#565c66]">{MODES[mode]?.tag || "Executive brief"}</p>
          <h1 className="text-[26px] font-bold uppercase tracking-[-0.01em] text-[#f5f1e8] mt-4" style={{ fontFamily: '"Switzer", "Instrument Sans", sans-serif' }}>
            The Next Shift
          </h1>
          <p className="text-[12.5px] text-[#8b9199] mt-2">{eventName}</p>
          <form onSubmit={laasOpp} className="mt-8">
            <input
              value={kode}
              onChange={(e) => { setKode(e.target.value.toUpperCase()); setKodeFeil(""); }}
              placeholder="ACCESS CODE"
              autoFocus
              maxLength={12}
              className="w-full bg-[#0a0e13] border border-white/[0.14] rounded-[2px] px-3 py-3 text-center font-mono text-[15px] tracking-[0.4em] uppercase text-[#f5f1e8] placeholder:text-[#3a4048] focus:outline-none focus:border-[#F1E75A]/60 transition-colors"
              data-testid="op-share-kode"
            />
            {kodeFeil && <p className="text-[11.5px] text-red-400 mt-2.5" data-testid="op-share-feil">{kodeFeil}</p>}
            <button
              type="submit"
              disabled={!kode.trim() || sjekker}
              className="w-full mt-4 bg-white text-[#070a0e] text-[13px] font-semibold rounded-[2px] px-4 py-3 hover:bg-[#f1e75a] transition-colors disabled:opacity-50"
              data-testid="op-share-aapne"
            >
              {sjekker ? "Checking …" : "Open brief"}
            </button>
          </form>
          <p className="font-mono text-[8.5px] uppercase tracking-[0.22em] text-[#3a4048] mt-7">Shared personally · not for distribution</p>
        </div>
      )}
    </div>
  );
}
